import type { TraversalViewSettings } from '../types';
import type { RelationshipDiagramViewModelStreamState } from '../viewModel/types';
import type {
  RelationshipsGroupNode,
  RelationshipsLeafNode,
  RelationshipsNode,
  RelationshipsLink,
  RelationshipsGroupLink,
  RelationshipsViewStreamedProps,
} from './types';
import { itemDepth } from './util';
import { edgesToLinks } from './edgesToLinks';
import { getAddedAndUpdatedItems } from '../viewModel/util';

type StreamedProps<TViewSettings extends TraversalViewSettings> =
  RelationshipsViewStreamedProps<TViewSettings>;
type ResetRelationshipsViewArgs<TViewSettings extends TraversalViewSettings> =
  Pick<
    RelationshipDiagramViewModelStreamState<TViewSettings>,
    'viewSettings' | 'viewModel'
  >;
/**
 * resets the Network View (aka Relationships View 2) props from the Relationship Diagram view model.
 * @returns the Network View properties.
 */
const resetRelationshipsView = <TViewSettings extends TraversalViewSettings>({
  viewSettings,
  viewModel,
}: ResetRelationshipsViewArgs<TViewSettings>): StreamedProps<TViewSettings> => {
  const {
    groups,
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
    referenceTypes,
    errors,
    hasClones,
    noConnectedComponents,
  } = viewModel;
  const leafNodes = getAddedAndUpdatedItems(
    viewModel.nodes
  ) satisfies RelationshipsLeafNode[];
  const groupNodes = [
    ...groups.add,
    ...groups.update,
  ].map<RelationshipsGroupNode>(groupId => groups.byId.get(groupId!)!);
  const graphNodes = [...leafNodes, ...groupNodes];

  const allEdges = getAddedAndUpdatedItems(viewModel.edges);

  const { links: graphLinks } = allEdges.reduce(edgesToLinks, {
    links: [],
    linksBySourceAndTarget: new Map(),
    viewModel,
  });
  const graphGroups = getAddedAndUpdatedItems(
    viewModel.groups
  ) satisfies RelationshipsGroupNode[];
  const groupChildren = new Map<string, RelationshipsNode[]>(
    graphGroups.map(graphGroup => [
      graphGroup.id,
      [
        ...graphNodes.filter(
          graphNode => graphNode.parent?.id === graphGroup.id
        ),
      ],
    ])
  );
  const groupLinks = graphGroups.flatMap<RelationshipsGroupLink>(graphGroup => {
    const children = [...groupChildren.get(graphGroup.id)!, graphGroup];
    return children.map(child => ({ source: child, target: graphGroup }));
  });
  const forceLinks: (RelationshipsLink | RelationshipsGroupLink)[] = [
    ...graphLinks,
    ...groupLinks,
  ];

  const groupDepth = new Map(
    graphGroups.map(graphGroup => [graphGroup.id, itemDepth(graphGroup)])
  );
  const maxGroupDepth = Math.max(0, ...Array.from(groupDepth.values()));

  return {
    viewSettings,
    viewModel: {
      graphNodes,
      graphGroups,
      groupChildren,
      graphLinks,
      forceLinks,
      maxGroupDepth,
      traversedOutgoingReferenceTypes,
      traversedIncomingReferenceTypes,
      referenceTypes,
      errors,
      hasClones,
      noConnectedComponents,
    },
    viewInstanceId: '',
  };
};
export { resetRelationshipsView };
